import { ERROR_CODES } from '@roll-web/constants';
import { CREDIT_ERROR_TIPS } from 'constants/errors/credit';
import { apiClient } from 'lib/apiClient';
import {
  convertErrorToErrorDescription,
  isArcApiError,
} from 'src/services/errors';
import {
  OnErrorCallback,
  OnSuccessInstantPaymentCallback,
  OnSuccessRedirectCallback,
} from 'src/services/orders/pay';
import { OrderDetail } from 'types/resources/orderDetail';
import {
  post3ds2ErrorResponse,
  post3ds2SuccessResponse,
} from '~/api/v1/orders/_orderId@string/payments/credit/3ds2';

const CREDIT_GMO_ERROR_DETAILS: Record<
  string,
  {
    detail: string;
    tips: string;
  }
> = {
  E01250010: {
    detail: 'ご登録のクレジットカード情報でシステム上のエラーが発生しました。',
    tips: 'お手数をおかけしますが、一度カード情報を削除し、再登録のうえで決済をお試しください。',
  },
};

export const payByCredit = async (
  orderId: OrderDetail['id'],
  input: {
    cardSeq: string;
    user_token: string;
    returnUrl: string;
    onSuccessCallback: OnSuccessInstantPaymentCallback;
    onSuccessRedirectCallback: OnSuccessRedirectCallback;
    onErrorCallback: OnErrorCallback;
  }
): Promise<void> => {
  const {
    cardSeq,
    user_token,
    onSuccessCallback,
    onSuccessRedirectCallback,
    onErrorCallback,
  } = input;
  if (cardSeq === '') {
    throw new Error(ERROR_CODES.CREDIT_CARD_NOT_SELECTED);
  }
  try {
    const res = await apiClient.v1.orders
      ._orderId(orderId)
      .payments.credit.$3ds2.post({
        headers: {
          Authorization: `Bearer ${user_token}`,
        },
        body: {
          cardSeq: cardSeq,
          redirectSuccessUrl: input.returnUrl,
          redirectErrorUrl: input.returnUrl,
        },
      });
    const result = post3ds2SuccessResponse.safeParse(res.body);
    if (result.success) {
      // 3DS2の認証が必要ない場合、決済が完了しているので結果表示
      if (result.data.acs === '0') {
        return await onSuccessCallback(orderId);
      }
      if (result.data.acs === '1' || result.data.acs === '2') {
        return await onSuccessRedirectCallback(
          orderId,
          result.data.redirectUrl
        );
      }
    } else {
      const errorTitle = 'クレジットカードでの購入に失敗しました';
      const errorResult = post3ds2ErrorResponse.safeParse(res.body);
      if (errorResult.success) {
        const errorDetails = errorResult.data.details.map((detail) => {
          const errorDetail = CREDIT_GMO_ERROR_DETAILS[detail.gmoErrorCode];
          return (
            errorDetail ?? {
              detail: '不明なエラーが発生しました。',
              tips: 'お手数をおかけしますが、時間をおいて再度お試しください。',
            }
          );
        });
        if (errorDetails.length > 0) {
          const errorDetail = errorDetails[0];
          onErrorCallback(errorTitle, errorDetail.detail, errorDetail.tips);
          return;
        }
      }

      if (isArcApiError(res.body)) {
        const errorTips = CREDIT_ERROR_TIPS[res.body.code];
        onErrorCallback(errorTitle, res.body.message, errorTips ?? '');
        return;
      }
      throw new Error('商品の購入に失敗しました');
    }
  } catch (e) {
    const error = convertErrorToErrorDescription(e, '商品の購入に失敗しました');
    onErrorCallback('商品の購入に失敗しました', error.message);
    throw error;
  }
};
